import {
    collection,
    doc,
    addDoc,
    updateDoc,
    deleteDoc,
    getDoc,
    getDocs,
    query,
    where,
    orderBy,
    onSnapshot,
    writeBatch,
    Timestamp
} from 'firebase/firestore';
import { db } from '../firebase-config';
import { ScheduleShift, ScheduleTemplate, SchedulePeriod, ScheduleUtils } from '../models/ScheduleModels';
import VacationService from './VacationService';

/**
 * ScheduleService
 * Handles all schedule-related Firebase operations with enhanced time slot support
 * and automatic vacation cascade sync.
 */
export class ScheduleService {
    // Time slot constants
    static TIME_SLOT_MINUTES = 15;
    static START_HOUR        = 6;
    static END_HOUR          = 23;

    /**
     * Get schedules collection reference.
     */
    static getSchedulesCollection(companyId, staffId) {
        return collection(db, 'companies', companyId, 'schedules', staffId, 'shifts');
    }

    /**
     * Get schedule templates collection reference.
     */
    static getTemplatesCollection(companyId) {
        return collection(db, 'companies', companyId, 'scheduleTemplates');
    }

    /**
     * Get holidays collection reference.
     */
    static getHolidaysCollection(companyId) {
        return collection(db, 'companies', companyId, 'holidays');
    }

    // ==================== Time Slot Validation ====================

    /**
     * Validate time slot data for 15-minute increments.
     */
    static validateTimeSlot(timeString) {
        if (!ScheduleUtils.isValidTimeString(timeString)) {
            return { isValid: false, error: 'Invalid time format' };
        }

        const minutes      = ScheduleUtils.parseTimeToMinutes(timeString);
        const quarterHours = minutes % this.TIME_SLOT_MINUTES;

        if (quarterHours !== 0) {
            return {
                isValid: false,
                error: `Time must be in ${this.TIME_SLOT_MINUTES}-minute increments`
            };
        }

        const hours = Math.floor(minutes / 60);
        if (hours < this.START_HOUR || hours > this.END_HOUR) {
            return {
                isValid: false,
                error: `Time must be between ${this.START_HOUR}:00 and ${this.END_HOUR}:00`
            };
        }

        return { isValid: true };
    }

    /**
     * Round time to nearest 15-minute slot.
     */
    static roundToTimeSlot(timeString) {
        return ScheduleUtils.roundToNearestQuarter(timeString);
    }

    /**
     * Validate shift for time slot requirements.
     */
    static validateShiftTimeSlots(shift) {
        const errors = [];

        const startValidation = this.validateTimeSlot(ScheduleUtils.formatTime(shift.startTime));
        if (!startValidation.isValid) {
            errors.push(`Start time: ${startValidation.error}`);
        }

        const endValidation = this.validateTimeSlot(ScheduleUtils.formatTime(shift.endTime));
        if (!endValidation.isValid) {
            errors.push(`End time: ${endValidation.error}`);
        }

        if (shift.expectedHours < (this.TIME_SLOT_MINUTES / 60)) {
            errors.push(`Minimum shift duration is ${this.TIME_SLOT_MINUTES} minutes`);
        }

        return {
            isValid: errors.length === 0,
            errors
        };
    }

    // ==================== Enhanced CRUD Operations ====================

    /**
     * Save weekly schedule for a staff member.
     * Creates or replaces shifts for a specific week.
     *
     * Cascade: reconcileVacationsWithShifts is called after the full
     * DELETE-then-CREATE completes, ensuring vacation documents for the
     * week are updated or deleted to match the new shift state.
     */
    static async saveSchedule(companyId, staffId, departmentId, weekStartDate, weekEndDate, shifts, createdBy) {
        try {
            console.log('Saving schedule:', { companyId, staffId, departmentId, weekStartDate, weekEndDate, shiftCount: shifts.length });

            if (!companyId || !staffId || !shifts || shifts.length === 0) {
                throw new Error('Fehlende erforderliche Parameter');
            }

            const scheduleShifts = [];

            for (const shiftData of shifts) {
                const startTimeStr = shiftData.startTime;
                const endTimeStr   = shiftData.endTime;
                const date         = shiftData.date;

                const startTime = ScheduleUtils.parseTimeToTimestamp(startTimeStr, date);
                const endTime   = ScheduleUtils.parseTimeToTimestamp(endTimeStr, date);

                const shift = new ScheduleShift({
                    staffId,
                    departmentId: shiftData.departmentId || departmentId,
                    date,
                    startTime,
                    endTime,
                    expectedHours: shiftData.totalMinutes / 60,
                    notes:         shiftData.notes || '',
                    status:        'published',
                    createdBy
                });

                scheduleShifts.push(shift);
            }

            const validationErrors = [];
            scheduleShifts.forEach((shift, index) => {
                const errors = shift.validate();
                if (errors.length > 0) {
                    validationErrors.push(`Schicht ${index + 1}: ${errors.join(', ')}`);
                }

                const timeSlotValidation = this.validateShiftTimeSlots(shift);
                if (!timeSlotValidation.isValid) {
                    validationErrors.push(`Schicht ${index + 1} Zeitslots: ${timeSlotValidation.errors.join(', ')}`);
                }
            });

            if (validationErrors.length > 0) {
                throw new Error(`Validierung fehlgeschlagen: ${validationErrors.join('; ')}`);
            }

            const deleteResult = await this.deleteScheduleRange(companyId, staffId, weekStartDate, weekEndDate);
            if (!deleteResult.success) {
                console.warn('Warning: Bestehende Schichten konnten nicht gelöscht werden:', deleteResult.error);
            }

            const result = await this.createShiftsBatch(companyId, scheduleShifts);

            if (!result.success) {
                throw new Error(result.error);
            }

            console.log(`Successfully saved ${result.count} shifts`);

            // Cascade: reconcile all vacations in this week against the new shift state.
            // This handles updates (duration changed), deletions (date no longer has a shift),
            // and confirms any updates already applied by createShiftsBatch.
            try {
                await VacationService.reconcileVacationsWithShifts(
                    companyId,
                    staffId,
                    weekStartDate,
                    weekEndDate,
                    result.shifts
                );
            } catch (error) {
                console.warn('Warning: Vacation reconciliation failed after schedule save (non-critical):', error);
            }

            return {
                success: true,
                message: `Erfolgreich ${result.count} Schichten gespeichert`,
                shifts:  result.shifts,
                count:   result.count
            };

        } catch (error) {
            console.error('Error saving schedule:', error);
            return {
                success: false,
                error: error.message
            };
        }
    }

    /**
     * Delete all schedule shifts for a specific date.
     *
     * Cascade: deleteVacationForDateIfExists is called after all shifts
     * on the date are confirmed deleted. No remaining shifts means any
     * vacation document on that date is now orphaned and must be removed.
     */
    static async deleteSchedule(companyId, staffId, dateISO) {
        try {
            console.log('Deleting schedule from Firebase:', { companyId, staffId, dateISO });

            const shiftsCollection = this.getSchedulesCollection(companyId, staffId);
            const q = query(
                shiftsCollection,
                where('date', '==', dateISO)
            );

            const querySnapshot = await getDocs(q);

            if (querySnapshot.empty) {
                console.warn('Warning: No schedule shifts found for date:', dateISO);
                return {
                    success:      true,
                    message:      'No shifts found for this date',
                    deletedCount: 0
                };
            }

            console.log(`Found ${querySnapshot.size} shift(s) to delete for ${dateISO}`);

            const batch = writeBatch(db);
            querySnapshot.docs.forEach(doc => {
                batch.delete(doc.ref);
            });

            await batch.commit();

            console.log(`Successfully deleted ${querySnapshot.size} shift(s) for ${dateISO}`);

            // Cascade: all shifts on this date are gone, delete any vacation document
            // that was tied to this date for this staff member.
            try {
                await VacationService.deleteVacationForDateIfExists(companyId, staffId, dateISO);
            } catch (error) {
                console.warn('Warning: Vacation cascade failed after schedule delete (non-critical):', error);
            }

            return {
                success:      true,
                message:      `Deleted ${querySnapshot.size} shift(s) for ${dateISO}`,
                deletedCount: querySnapshot.size
            };

        } catch (error) {
            console.error('Error deleting schedule from Firebase:', error);
            return {
                success:      false,
                error:        error.message,
                deletedCount: 0
            };
        }
    }

    /**
     * Create a new schedule shift with time slot validation.
     *
     * Cascade: updateVacationDurationForDate is called after the shift is
     * written. If a vacation already exists on this date, its duration is
     * updated to reflect the new shift. If no vacation exists, nothing happens.
     */
    static async createShift(companyId, shiftData) {
        try {
            console.log('Creating shift:', { companyId, shiftData });

            const shift = shiftData instanceof ScheduleShift ? shiftData : new ScheduleShift(shiftData);

            const validationErrors = shift.validate();
            if (validationErrors.length > 0) {
                throw new Error(`Validation failed: ${validationErrors.join(', ')}`);
            }

            const timeSlotValidation = this.validateShiftTimeSlots(shift);
            if (!timeSlotValidation.isValid) {
                throw new Error(`Time slot validation failed: ${timeSlotValidation.errors.join(', ')}`);
            }

            const conflicts = await this.checkTimeSlotConflicts(companyId, shift);
            if (conflicts.length > 0) {
                throw new Error(`Shift conflicts with existing schedule: ${conflicts.map(c =>
                    `${ScheduleUtils.formatTime(c.startTime)}-${ScheduleUtils.formatTime(c.endTime)}`
                ).join(', ')}`);
            }

            const shiftsCollection = this.getSchedulesCollection(companyId, shift.staffId);
            const docRef = await addDoc(shiftsCollection, shift.toFirestore());

            console.log(`Shift created with ID: ${docRef.id}`);

            // Cascade: update vacation duration for this date if a vacation exists.
            try {
                const totalMinutes = Math.round((shift.expectedHours || 0) * 60);
                await VacationService.updateVacationDurationForDate(
                    companyId,
                    shift.staffId,
                    shift.date,
                    totalMinutes
                );
            } catch (error) {
                console.warn('Warning: Vacation cascade failed after shift create (non-critical):', error);
            }

            return {
                success: true,
                shiftId: docRef.id,
                shift:   { ...shift, id: docRef.id }
            };
        } catch (error) {
            console.error('Error creating shift:', error);
            return {
                success: false,
                error:   error.message
            };
        }
    }

    /**
     * Create shift from selected time slots.
     */
    static async createShiftFromTimeSlots(companyId, staffId, date, selectedSlots, departmentId, notes = '') {
        try {
            if (!selectedSlots || selectedSlots.length === 0) {
                throw new Error('No time slots selected');
            }

            const times = selectedSlots
                .map(slotId => slotId.split('-')[2])
                .filter(time => time && ScheduleUtils.isValidTimeString(time))
                .sort();

            if (times.length === 0) {
                throw new Error('Invalid time slot selection');
            }

            const startTime = times[0];
            const endTime   = ScheduleUtils.addMinutesToTime(times[times.length - 1], this.TIME_SLOT_MINUTES);

            const shiftData = {
                staffId,
                departmentId,
                date,
                startTime:     ScheduleUtils.parseTimeToTimestamp(startTime, date),
                endTime:       ScheduleUtils.parseTimeToTimestamp(endTime, date),
                notes,
                expectedHours: ScheduleUtils.calculateDurationHours(startTime, endTime)
            };

            return await this.createShift(companyId, shiftData);
        } catch (error) {
            console.error('Error creating shift from time slots:', error);
            return {
                success: false,
                error:   error.message
            };
        }
    }

    /**
     * Update an existing schedule shift with time slot validation.
     *
     * Cascade: if the shift's date changed, the old date is evaluated for
     * remaining shifts (delete or update vacation accordingly). The new
     * date's vacation duration is then updated to match the updated shift.
     */
    static async updateShift(companyId, staffId, shiftId, updates) {
        try {
            console.log('Updating shift:', { companyId, staffId, shiftId, updates });

            const shiftRef = doc(db, 'companies', companyId, 'schedules', staffId, 'shifts', shiftId);

            const currentShiftDoc = await getDoc(shiftRef);
            if (!currentShiftDoc.exists()) {
                throw new Error('Shift not found');
            }

            const currentShift  = ScheduleShift.fromFirestore(currentShiftDoc);
            const updatedShift  = new ScheduleShift({ ...currentShift, ...updates, id: shiftId });

            const validationErrors = updatedShift.validate();
            if (validationErrors.length > 0) {
                throw new Error(`Validation failed: ${validationErrors.join(', ')}`);
            }

            const timeSlotValidation = this.validateShiftTimeSlots(updatedShift);
            if (!timeSlotValidation.isValid) {
                throw new Error(`Time slot validation failed: ${timeSlotValidation.errors.join(', ')}`);
            }

            const conflicts = await this.checkTimeSlotConflicts(companyId, updatedShift, shiftId);
            if (conflicts.length > 0) {
                throw new Error(`Updated shift conflicts with existing schedule: ${conflicts.map(c =>
                    `${ScheduleUtils.formatTime(c.startTime)}-${ScheduleUtils.formatTime(c.endTime)}`
                ).join(', ')}`);
            }

            await updateDoc(shiftRef, {
                ...updatedShift.toFirestore(),
                updatedAt: Timestamp.now()
            });

            console.log(`Shift updated: ${shiftId}`);

            const oldDate = currentShift.date;
            const newDate = updatedShift.date;

            // Cascade: if the shift moved to a different date, evaluate the old date.
            // Query the remaining shifts on the old date (the moved shift is no longer
            // there after updateDoc). Delete or update the vacation accordingly.
            if (oldDate !== newDate) {
                try {
                    const remainingQuery    = query(
                        this.getSchedulesCollection(companyId, staffId),
                        where('date', '==', oldDate)
                    );
                    const remainingSnapshot = await getDocs(remainingQuery);

                    if (remainingSnapshot.empty) {
                        await VacationService.deleteVacationForDateIfExists(companyId, staffId, oldDate);
                    } else {
                        const remainingMinutes = remainingSnapshot.docs.reduce((sum, docSnap) => {
                            const d = docSnap.data();
                            return sum + (typeof d.totalMinutes === 'number'
                                ? d.totalMinutes
                                : Math.round((d.expectedHours || 0) * 60));
                        }, 0);
                        await VacationService.updateVacationDurationForDate(
                            companyId,
                            staffId,
                            oldDate,
                            remainingMinutes
                        );
                    }
                } catch (error) {
                    console.warn('Warning: Vacation cascade failed for old date after shift update (non-critical):', error);
                }
            }

            // Cascade: update vacation duration on the new (or unchanged) date.
            try {
                const newTotalMinutes = Math.round((updatedShift.expectedHours || 0) * 60);
                await VacationService.updateVacationDurationForDate(
                    companyId,
                    staffId,
                    newDate,
                    newTotalMinutes
                );
            } catch (error) {
                console.warn('Warning: Vacation cascade failed for new date after shift update (non-critical):', error);
            }

            return {
                success: true,
                shift:   updatedShift
            };
        } catch (error) {
            console.error('Error updating shift:', error);
            return {
                success: false,
                error:   error.message
            };
        }
    }

    /**
     * Delete a schedule shift.
     *
     * Cascade: after confirmed deletion, the remaining shifts on the same date
     * are queried. If none remain, the vacation for that date is deleted.
     * If others remain, the vacation duration is updated to their total minutes.
     */
    static async deleteShift(companyId, staffId, shiftId) {
        try {
            console.log('Deleting shift:', { companyId, staffId, shiftId });

            const shiftRef = doc(db, 'companies', companyId, 'schedules', staffId, 'shifts', shiftId);

            const shiftDoc = await getDoc(shiftRef);
            if (shiftDoc.exists()) {
                const shiftData = shiftDoc.data();

                await deleteDoc(shiftRef);

                console.log(`Shift deleted: ${shiftId}`);

                // Cascade: check what shifts remain on this date after deletion.
                try {
                    const remainingQuery    = query(
                        this.getSchedulesCollection(companyId, staffId),
                        where('date', '==', shiftData.date)
                    );
                    const remainingSnapshot = await getDocs(remainingQuery);

                    if (remainingSnapshot.empty) {
                        // No shifts left on this date - vacation is now orphaned, delete it.
                        await VacationService.deleteVacationForDateIfExists(
                            companyId,
                            staffId,
                            shiftData.date
                        );
                    } else {
                        // Other shifts still exist - update vacation duration to their total.
                        const remainingMinutes = remainingSnapshot.docs.reduce((sum, docSnap) => {
                            const d = docSnap.data();
                            return sum + (typeof d.totalMinutes === 'number'
                                ? d.totalMinutes
                                : Math.round((d.expectedHours || 0) * 60));
                        }, 0);
                        await VacationService.updateVacationDurationForDate(
                            companyId,
                            staffId,
                            shiftData.date,
                            remainingMinutes
                        );
                    }
                } catch (error) {
                    console.warn('Warning: Vacation cascade failed after shift delete (non-critical):', error);
                }
            } else {
                await deleteDoc(shiftRef);
                console.log(`Shift deleted (no data found): ${shiftId}`);
            }

            return {
                success: true,
                shiftId
            };
        } catch (error) {
            console.error('Error deleting shift:', error);
            return {
                success: false,
                error:   error.message
            };
        }
    }

    /**
     * Get schedule shifts for a staff member within date range.
     *
     * orderBy('startTime', 'asc') has been removed from this query.
     * Legacy documents store startTime as a Firestore Timestamp while new
     * documents store it as a plain "HH:MM" string. Firestore places the two
     * types in separate sort buckets, producing an incorrect secondary sort
     * for any date that contains a mix of both formats. Removing the server-side
     * sort is safe because the iOS ScheduleDataService re-sorts by startTime
     * string after receiving the data, and the web calendar components sort
     * client-side as well.
     */
    static async getScheduleForStaff(companyId, staffId, startDate, endDate) {
        try {
            const shiftsCollection = this.getSchedulesCollection(companyId, staffId);
            const q = query(
                shiftsCollection,
                where('date', '>=', startDate),
                where('date', '<=', endDate),
                orderBy('date', 'asc')
                // orderBy('startTime') intentionally omitted - see method comment above
            );

            const querySnapshot = await getDocs(q);
            const shifts = querySnapshot.docs.map(doc => ScheduleShift.fromFirestore(doc));

            return {
                success: true,
                shifts
            };
        } catch (error) {
            console.error('Error getting schedule for staff:', error);
            return {
                success: false,
                error:   error.message,
                shifts:  []
            };
        }
    }

    /**
     * Get schedules for multiple staff members within date range.
     */
    static async getScheduleForStaffList(companyId, staffIds, startDate, endDate) {
        try {
            const allShifts = {};

            for (const staffId of staffIds) {
                const result = await this.getScheduleForStaff(companyId, staffId, startDate, endDate);
                if (result.success) {
                    allShifts[staffId] = result.shifts;
                }
            }

            return {
                success:   true,
                schedules: allShifts
            };
        } catch (error) {
            console.error('Error getting schedules for staff list:', error);
            return {
                success:   false,
                error:     error.message,
                schedules: {}
            };
        }
    }

    // ==================== Enhanced Time Slot Conflict Detection ====================

    /**
     * Check for time slot conflicts with granular detection.
     */
    static async checkTimeSlotConflicts(companyId, shift, excludeShiftId = null) {
        try {
            const shiftsResult = await this.getScheduleForStaff(
                companyId,
                shift.staffId,
                shift.date,
                shift.date
            );

            if (!shiftsResult.success) {
                return [];
            }

            const shiftStartMinutes = ScheduleUtils.timestampToMinutes(shift.startTime);
            const shiftEndMinutes   = ScheduleUtils.timestampToMinutes(shift.endTime);

            const conflicts = shiftsResult.shifts.filter(existingShift => {
                if (excludeShiftId && existingShift.id === excludeShiftId) {
                    return false;
                }

                const existingStartMinutes = ScheduleUtils.timestampToMinutes(existingShift.startTime);
                const existingEndMinutes   = ScheduleUtils.timestampToMinutes(existingShift.endTime);

                return ScheduleUtils.timeRangesOverlap(
                    ScheduleUtils.minutesToTimeString(shiftStartMinutes),
                    ScheduleUtils.minutesToTimeString(shiftEndMinutes),
                    ScheduleUtils.minutesToTimeString(existingStartMinutes),
                    ScheduleUtils.minutesToTimeString(existingEndMinutes)
                );
            });

            return conflicts;
        } catch (error) {
            console.error('Error checking time slot conflicts:', error);
            return [];
        }
    }

    /**
     * Get occupied time slots for a staff member on a specific date.
     */
    static async getOccupiedTimeSlots(companyId, staffId, date) {
        try {
            const shiftsResult = await this.getScheduleForStaff(companyId, staffId, date, date);
            if (!shiftsResult.success) {
                return { success: false, error: shiftsResult.error, occupiedSlots: [] };
            }

            const occupiedSlots = [];

            shiftsResult.shifts.forEach(shift => {
                const startTime = ScheduleUtils.formatTime(shift.startTime);
                const endTime   = ScheduleUtils.formatTime(shift.endTime);
                const slots     = ScheduleUtils.getTimeSlotsInRange(startTime, endTime);
                occupiedSlots.push(...slots);
            });

            return {
                success:       true,
                occupiedSlots: [...new Set(occupiedSlots)].sort()
            };
        } catch (error) {
            console.error('Error getting occupied time slots:', error);
            return {
                success:       false,
                error:         error.message,
                occupiedSlots: []
            };
        }
    }

    /**
     * Check if specific time slots are available.
     */
    static async areTimeSlotsAvailable(companyId, staffId, date, timeSlots) {
        try {
            const occupiedResult = await this.getOccupiedTimeSlots(companyId, staffId, date);
            if (!occupiedResult.success) {
                return { success: false, error: occupiedResult.error };
            }

            const conflicts = timeSlots.filter(slot => occupiedResult.occupiedSlots.includes(slot));

            return {
                success:       true,
                available:     conflicts.length === 0,
                conflicts,
                conflictCount: conflicts.length
            };
        } catch (error) {
            console.error('Error checking time slot availability:', error);
            return {
                success: false,
                error:   error.message
            };
        }
    }

    // ==================== Enhanced Bulk Operations ====================

    /**
     * Create multiple shifts in batch with time slot validation.
     *
     * Cascade: after the batch commits successfully, updateVacationDurationForDate
     * is called for each created shift. This covers standalone batch creates
     * (template apply, week copy, repeated shifts). When called from saveSchedule,
     * the reconcileVacationsWithShifts call at the end of saveSchedule provides
     * the authoritative final vacation state for the week.
     */
    static async createShiftsBatch(companyId, shifts) {
        try {
            console.log(`Creating batch of ${shifts.length} shifts`);

            const batch          = writeBatch(db);
            const createdShifts  = [];
            const validationErrors = [];

            for (let i = 0; i < shifts.length; i++) {
                const shiftData = shifts[i];
                const shift     = shiftData instanceof ScheduleShift ? shiftData : new ScheduleShift(shiftData);

                const basicValidation = shift.validate();
                if (basicValidation.length > 0) {
                    validationErrors.push(`Shift ${i + 1}: ${basicValidation.join(', ')}`);
                    continue;
                }

                const timeSlotValidation = this.validateShiftTimeSlots(shift);
                if (!timeSlotValidation.isValid) {
                    validationErrors.push(`Shift ${i + 1} time slots: ${timeSlotValidation.errors.join(', ')}`);
                    continue;
                }

                const shiftsCollection = this.getSchedulesCollection(companyId, shift.staffId);
                const newShiftRef      = doc(shiftsCollection);
                batch.set(newShiftRef, shift.toFirestore());

                createdShifts.push({ ...shift, id: newShiftRef.id });
            }

            if (validationErrors.length > 0) {
                throw new Error(`Validation errors: ${validationErrors.join('; ')}`);
            }

            if (createdShifts.length === 0) {
                throw new Error('No valid shifts to create');
            }

            await batch.commit();

            console.log(`Batch created ${createdShifts.length} shifts`);

            // Cascade: update vacation duration for each created shift's date.
            // Only updates existing vacation documents, never creates new ones.
            try {
                for (const shift of createdShifts) {
                    const totalMinutes = typeof shift.totalMinutes === 'number'
                        ? shift.totalMinutes
                        : Math.round((shift.expectedHours || 0) * 60);
                    await VacationService.updateVacationDurationForDate(
                        companyId,
                        shift.staffId,
                        shift.date,
                        totalMinutes
                    );
                }
            } catch (error) {
                console.warn('Warning: Vacation cascade failed after batch shift create (non-critical):', error);
            }

            return {
                success: true,
                shifts:  createdShifts,
                count:   createdShifts.length
            };
        } catch (error) {
            console.error('Error creating shifts batch:', error);
            return {
                success: false,
                error:   error.message
            };
        }
    }

    /**
     * Create multiple shifts from time slot selections.
     */
    static async createShiftsFromTimeSlotSelections(companyId, selections) {
        try {
            const shifts = selections.map(selection => {
                const { staffId, date, selectedSlots, departmentId, notes } = selection;

                if (!selectedSlots || selectedSlots.length === 0) {
                    throw new Error(`No time slots selected for ${staffId} on ${date}`);
                }

                const times = selectedSlots
                    .map(slotId => slotId.split('-')[2])
                    .filter(time => time && ScheduleUtils.isValidTimeString(time))
                    .sort();

                if (times.length === 0) {
                    throw new Error(`Invalid time slots for ${staffId} on ${date}`);
                }

                const startTime = times[0];
                const endTime   = ScheduleUtils.addMinutesToTime(times[times.length - 1], this.TIME_SLOT_MINUTES);

                return new ScheduleShift({
                    staffId,
                    departmentId,
                    date,
                    startTime:     ScheduleUtils.parseTimeToTimestamp(startTime, date),
                    endTime:       ScheduleUtils.parseTimeToTimestamp(endTime, date),
                    notes:         notes || '',
                    expectedHours: ScheduleUtils.calculateDurationHours(startTime, endTime)
                });
            });

            return await this.createShiftsBatch(companyId, shifts);
        } catch (error) {
            console.error('Error creating shifts from time slot selections:', error);
            return {
                success: false,
                error:   error.message
            };
        }
    }

    /**
     * Apply schedule template to multiple dates for a staff member.
     */
    static async applyTemplateToStaff(companyId, staffId, templateId, dates) {
        try {
            const template = await this.getScheduleTemplate(companyId, templateId);
            if (!template.success) {
                throw new Error('Template not found');
            }

            const generatedShifts = template.template.applyToStaff(staffId, dates);
            return await this.createShiftsBatch(companyId, generatedShifts);
        } catch (error) {
            console.error('Error applying template:', error);
            return {
                success: false,
                error:   error.message
            };
        }
    }

    /**
     * Copy schedule pattern from one week to another with time slot precision.
     *
     * FIXED: The new date string is now built with this.formatDate() which reads
     * local year/month/day values. The previous implementation used
     * shiftDate.toISOString().split('T')[0] which converts to UTC first.
     * For Germany in CEST (UTC+2) this caused any shift copied in the evening
     * to land one day behind the intended target date.
     */
    static async copyWeekSchedule(companyId, staffId, sourceWeekStart, targetWeekStart) {
        try {
            const sourceWeek = ScheduleUtils.getWeekBoundaries(sourceWeekStart);

            const sourceResult = await this.getScheduleForStaff(
                companyId,
                staffId,
                sourceWeek.start,
                sourceWeek.end
            );

            if (!sourceResult.success || sourceResult.shifts.length === 0) {
                throw new Error('No shifts found in source week');
            }

            const sourceDate = new Date(sourceWeekStart);
            const targetDate = new Date(targetWeekStart);
            const dayOffset  = Math.floor((targetDate - sourceDate) / (1000 * 60 * 60 * 24));

            const newShifts = sourceResult.shifts.map(shift => {
                const shiftDate = new Date(shift.date);
                shiftDate.setDate(shiftDate.getDate() + dayOffset);

                // FIXED: this.formatDate() uses local getFullYear/getMonth/getDate,
                // eliminating the UTC offset that toISOString() introduced.
                const newDate = this.formatDate(shiftDate);

                return shift.copyForDate(newDate);
            });

            return await this.createShiftsBatch(companyId, newShifts);
        } catch (error) {
            console.error('Error copying week schedule:', error);
            return {
                success: false,
                error:   error.message
            };
        }
    }

    // ==================== REPEAT FUNCTIONALITY ====================

    /**
     * Generate repeated shifts based on recurrence pattern.
     * Skips the original date and starts from the next occurrence.
     */
    static generateRepeatedShifts(baseShift, recurrence, endDate) {
        const shifts    = [];
        const startDate = new Date(baseShift.date);
        const end       = new Date(endDate);

        const maxDate = new Date(startDate);
        maxDate.setMonth(maxDate.getMonth() + 12);
        const finalEndDate = end > maxDate ? maxDate : end;

        let currentDate = new Date(startDate);

        switch (recurrence) {
            case 'WEEKLY':
                currentDate.setDate(currentDate.getDate() + 7);
                break;
            case 'BIWEEKLY':
                currentDate.setDate(currentDate.getDate() + 14);
                break;
            case 'MONTHLY':
                currentDate.setMonth(currentDate.getMonth() + 1);
                break;
            default:
                return shifts;
        }

        while (currentDate <= finalEndDate) {
            const dateString = this.formatDate(currentDate);

            shifts.push({
                ...baseShift,
                date: dateString,
                id:   null
            });

            switch (recurrence) {
                case 'WEEKLY':
                    currentDate.setDate(currentDate.getDate() + 7);
                    break;
                case 'BIWEEKLY':
                    currentDate.setDate(currentDate.getDate() + 14);
                    break;
                case 'MONTHLY':
                    currentDate.setMonth(currentDate.getMonth() + 1);
                    break;
                default:
                    return shifts;
            }
        }

        return shifts;
    }

    /**
     * Check for duplicate shifts (handles both string and Timestamp formats).
     */
    static async findDuplicateShifts(companyId, staffId, shifts) {
        try {
            const shiftsRef    = this.getSchedulesCollection(companyId, staffId);
            const existingShifts = await getDocs(shiftsRef);

            const existingMap = new Map();
            existingShifts.forEach(doc => {
                const data = doc.data();

                let startTime, endTime;

                if (typeof data.startTime === 'string') {
                    startTime = data.startTime;
                } else if (data.startTime?.toDate) {
                    const date = data.startTime.toDate();
                    startTime = `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
                } else {
                    console.warn('Unknown startTime format:', data.startTime);
                    return;
                }

                if (typeof data.endTime === 'string') {
                    endTime = data.endTime;
                } else if (data.endTime?.toDate) {
                    const date = data.endTime.toDate();
                    endTime = `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
                } else {
                    console.warn('Unknown endTime format:', data.endTime);
                    return;
                }

                const key = `${data.date}-${startTime}-${endTime}`;
                existingMap.set(key, doc.id);
            });

            return shifts.filter(shift => {
                const startTime = typeof shift.startTime === 'string' ? shift.startTime : shift.startTime;
                const endTime   = typeof shift.endTime   === 'string' ? shift.endTime   : shift.endTime;
                const key       = `${shift.date}-${startTime}-${endTime}`;
                return !existingMap.has(key);
            });
        } catch (error) {
            console.error('Error checking for duplicate shifts:', error);
            return shifts;
        }
    }

    /**
     * Create repeated shifts with batch write AND update original shift.
     *
     * Cascade: after all shift chunks are committed, updateVacationDurationForDate
     * is called for each newly created repeated shift date.
     */
    static async createRepeatedShifts(companyId, staffId, shiftId, baseShift, recurrence, endDate, userId) {
        try {
            console.log('Creating repeated shifts:', { companyId, staffId, shiftId, recurrence, endDate });

            const batchId = `batch_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

            const allShifts = this.generateRepeatedShifts(
                {
                    ...baseShift,
                    batchId,
                    recurrence,
                    repeatEnd: endDate
                },
                recurrence,
                endDate
            );

            console.log(`Generated ${allShifts.length} repeated shift(s)`);

            const uniqueShifts = await this.findDuplicateShifts(companyId, staffId, allShifts);

            console.log(`${uniqueShifts.length} unique shift(s) after duplicate check`);

            if (uniqueShifts.length === 0) {
                throw new Error('Alle Zeitslots existieren bereits');
            }

            const scheduleShifts = uniqueShifts.map(shiftData => {
                return new ScheduleShift({
                    staffId,
                    departmentId: shiftData.departmentId,
                    date:         shiftData.date,
                    startTime:    ScheduleUtils.parseTimeToTimestamp(shiftData.startTime, shiftData.date),
                    endTime:      ScheduleUtils.parseTimeToTimestamp(shiftData.endTime, shiftData.date),
                    expectedHours: ScheduleUtils.calculateDurationHours(shiftData.startTime, shiftData.endTime),
                    notes:         shiftData.notes || '',
                    status:        'published',
                    createdBy:     userId,
                    createdAt:     new Date(),
                    batchId:       batchId,
                    recurrence:    recurrence,
                    repeatEnd:     endDate
                });
            });

            const chunks       = this.chunkArray(scheduleShifts, 500);
            let totalCreated   = 0;

            for (const chunk of chunks) {
                const batch = writeBatch(db);

                chunk.forEach(shift => {
                    const shiftRef    = doc(this.getSchedulesCollection(companyId, staffId));
                    const firestoreData = shift.toFirestore();
                    firestoreData.batchId    = batchId;
                    firestoreData.recurrence = recurrence;
                    firestoreData.repeatEnd  = endDate;
                    batch.set(shiftRef, firestoreData);
                });

                await batch.commit();
                totalCreated += chunk.length;
            }

            console.log(`Created ${totalCreated} repeated shift(s) in Firestore`);

            const originalShiftRef = doc(
                this.getSchedulesCollection(companyId, staffId),
                shiftId
            );

            await updateDoc(originalShiftRef, {
                batchId:    batchId,
                recurrence: recurrence,
                repeatEnd:  endDate,
                updatedAt:  Timestamp.now()
            });

            console.log(`Updated original shift ${shiftId} with batchId: ${batchId}`);

            // Cascade: update vacation duration for each repeated shift date.
            try {
                for (const shift of scheduleShifts) {
                    const totalMinutes = Math.round((shift.expectedHours || 0) * 60);
                    await VacationService.updateVacationDurationForDate(
                        companyId,
                        staffId,
                        shift.date,
                        totalMinutes
                    );
                }
            } catch (error) {
                console.warn('Warning: Vacation cascade failed after creating repeated shifts (non-critical):', error);
            }

            return totalCreated;
        } catch (error) {
            console.error('Error creating repeated shifts:', error);
            throw error;
        }
    }

    /**
     * Delete future shifts in a batch.
     *
     * Cascade: after confirmed deletion, the remaining shifts in the affected
     * date range are queried and passed to reconcileVacationsWithShifts. This
     * handles both deletion of orphaned vacations and duration updates where
     * non-batch shifts still exist on deleted dates.
     */
    static async deleteFutureBatchShifts(companyId, staffId, batchId, fromDate) {
        try {
            console.log('Deleting future batch shifts:', { companyId, staffId, batchId, fromDate });

            const shiftsRef = this.getSchedulesCollection(companyId, staffId);
            const q         = query(
                shiftsRef,
                where('batchId', '==', batchId),
                where('date', '>=', fromDate)
            );

            const snapshot = await getDocs(q);

            console.log(`Found ${snapshot.size} shift(s) to delete`);

            if (snapshot.empty) return 0;

            // Collect dates for cascade before deletion
            const dates = new Set();
            snapshot.docs.forEach(doc => {
                dates.add(doc.data().date);
            });

            const chunks       = this.chunkArray(snapshot.docs, 500);
            let totalDeleted   = 0;

            for (const chunk of chunks) {
                const batch = writeBatch(db);
                chunk.forEach(doc => {
                    batch.delete(doc.ref);
                });
                await batch.commit();
                totalDeleted += chunk.length;
            }

            console.log(`Successfully deleted ${totalDeleted} shift(s)`);

            // Cascade: reconcile vacations for the affected date range against
            // whatever shifts remain after the batch delete.
            if (dates.size > 0) {
                try {
                    const sortedDates = [...dates].sort();
                    const minDate     = sortedDates[0];
                    const maxDate     = sortedDates[sortedDates.length - 1];

                    const remainingResult = await this.getScheduleForStaff(
                        companyId,
                        staffId,
                        minDate,
                        maxDate
                    );
                    const remainingShifts = remainingResult.success ? remainingResult.shifts : [];

                    await VacationService.reconcileVacationsWithShifts(
                        companyId,
                        staffId,
                        minDate,
                        maxDate,
                        remainingShifts
                    );
                } catch (error) {
                    console.warn('Warning: Vacation cascade failed after future batch shift delete (non-critical):', error);
                }
            }

            return totalDeleted;
        } catch (error) {
            console.error('Error deleting future batch shifts:', error);
            throw error;
        }
    }

    /**
     * Replace future batch shifts with new pattern.
     */
    static async replaceFutureBatchShifts(companyId, staffId, originalShift, recurrence, endDate, userId) {
        try {
            const deletedCount = await this.deleteFutureBatchShifts(
                companyId,
                staffId,
                originalShift.batchId,
                originalShift.date
            );

            const createdCount = await this.createRepeatedShifts(
                companyId,
                staffId,
                originalShift.id,
                originalShift,
                recurrence,
                endDate,
                userId
            );

            return {
                deletedCount,
                createdCount
            };
        } catch (error) {
            console.error('Error replacing future batch shifts:', error);
            throw error;
        }
    }

    /**
     * Utility: Format date to YYYY-MM-DD using local time.
     * All date string generation in this service must go through this method.
     * Never use toISOString() for date strings - it returns UTC and causes
     * a one-day offset for timezones ahead of UTC (e.g. Germany CEST = UTC+2).
     */
    static formatDate(date) {
        const year  = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day   = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    }

    /**
     * Utility: Chunk array into smaller arrays.
     */
    static chunkArray(array, size) {
        const chunks = [];
        for (let i = 0; i < array.length; i += size) {
            chunks.push(array.slice(i, i + size));
        }
        return chunks;
    }

    // ==================== Schedule Templates ====================

    /**
     * Create schedule template.
     */
    static async createScheduleTemplate(companyId, template) {
        try {
            if (!(template instanceof ScheduleTemplate)) {
                template = new ScheduleTemplate(template);
            }

            const templatesCollection = this.getTemplatesCollection(companyId);
            const docRef = await addDoc(templatesCollection, template.toFirestore());

            return {
                success:    true,
                templateId: docRef.id,
                template:   { ...template, id: docRef.id }
            };
        } catch (error) {
            console.error('Error creating schedule template:', error);
            return {
                success: false,
                error:   error.message
            };
        }
    }

    /**
     * Get schedule template.
     */
    static async getScheduleTemplate(companyId, templateId) {
        try {
            const templateRef = doc(db, 'companies', companyId, 'scheduleTemplates', templateId);
            const templateDoc = await getDoc(templateRef);

            if (!templateDoc.exists()) {
                throw new Error('Template not found');
            }

            const template = ScheduleTemplate.fromFirestore(templateDoc);

            return {
                success:  true,
                template
            };
        } catch (error) {
            console.error('Error getting schedule template:', error);
            return {
                success: false,
                error:   error.message
            };
        }
    }

    /**
     * Get all schedule templates for a company.
     */
    static async getScheduleTemplates(companyId, departmentId = null) {
        try {
            const templatesCollection = this.getTemplatesCollection(companyId);
            let q = query(templatesCollection, orderBy('name', 'asc'));

            if (departmentId) {
                q = query(templatesCollection, where('departmentId', '==', departmentId), orderBy('name', 'asc'));
            }

            const querySnapshot = await getDocs(q);
            const templates = querySnapshot.docs.map(doc => ScheduleTemplate.fromFirestore(doc));

            return {
                success:   true,
                templates
            };
        } catch (error) {
            console.error('Error getting schedule templates:', error);
            return {
                success:   false,
                error:     error.message,
                templates: []
            };
        }
    }

    // ==================== Holiday Integration ====================

    /**
     * Get federal holidays for a date range.
     */
    static async getHolidays(companyId, startDate, endDate) {
        try {
            const holidaysCollection = this.getHolidaysCollection(companyId);
            const q = query(
                holidaysCollection,
                where('dateString', '>=', startDate),
                where('dateString', '<=', endDate),
                orderBy('dateString', 'asc')
            );

            const querySnapshot = await getDocs(q);
            const holidays = querySnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));

            return {
                success:  true,
                holidays
            };
        } catch (error) {
            console.error('Error getting holidays:', error);
            return {
                success:  false,
                error:    error.message,
                holidays: []
            };
        }
    }

    /**
     * Get schedule period with holiday adjustments and time slot analysis.
     */
    static async getSchedulePeriod(companyId, staffId, startDate, endDate) {
        try {
            const shiftsResult = await this.getScheduleForStaff(companyId, staffId, startDate, endDate);
            if (!shiftsResult.success) {
                throw new Error(shiftsResult.error);
            }

            const holidaysResult = await this.getHolidays(companyId, startDate, endDate);
            if (!holidaysResult.success) {
                throw new Error(holidaysResult.error);
            }

            const period = new SchedulePeriod({
                staffId,
                startDate,
                endDate,
                shifts:   shiftsResult.shifts,
                holidays: holidaysResult.holidays
            });

            const timeSlotStats = this.calculateTimeSlotStatistics(shiftsResult.shifts);

            return {
                success:       true,
                period,
                totalHours:    period.getTotalScheduledHours(),
                adjustedHours: period.getAdjustedHours(),
                conflicts:     period.getShiftConflicts(),
                timeSlotStats
            };
        } catch (error) {
            console.error('Error getting schedule period:', error);
            return {
                success: false,
                error:   error.message
            };
        }
    }

    // ==================== Enhanced Conflict Detection ====================

    /**
     * Check for shift conflicts (legacy method for backward compatibility).
     */
    static async checkShiftConflicts(companyId, shift, excludeShiftId = null) {
        return await this.checkTimeSlotConflicts(companyId, shift, excludeShiftId);
    }

    // ==================== Enhanced Real-time Listeners ====================

    /**
     * Listen to schedule changes for a staff member with enhanced time slot support.
     *
     * orderBy('startTime', 'asc') has been removed from this query for the same
     * reason as getScheduleForStaff: mixed Timestamp/string types in the startTime
     * field produce an incorrect secondary sort. Client-side components sort by
     * startTime string after receiving the snapshot.
     */
    static subscribeToStaffSchedule(companyId, staffId, startDate, endDate, callback) {
        try {
            const shiftsCollection = this.getSchedulesCollection(companyId, staffId);
            const q = query(
                shiftsCollection,
                where('date', '>=', startDate),
                where('date', '<=', endDate),
                orderBy('date', 'asc')
                // orderBy('startTime') intentionally omitted - see method comment above
            );

            const unsubscribe = onSnapshot(q, (querySnapshot) => {
                const shifts = querySnapshot.docs.map(doc => ScheduleShift.fromFirestore(doc));

                const timeSlotStats = this.calculateTimeSlotStatistics(shifts);

                callback({
                    success:     true,
                    shifts,
                    staffId,
                    timeSlotStats,
                    totalHours: shifts.reduce((sum, shift) => sum + shift.expectedHours, 0)
                });
            }, (error) => {
                console.error('Error in schedule subscription:', error);
                callback({
                    success: false,
                    error:   error.message,
                    shifts:  [],
                    staffId
                });
            });

            return unsubscribe;
        } catch (error) {
            console.error('Error setting up schedule subscription:', error);
            return null;
        }
    }

    /**
     * Listen to schedule changes for multiple staff members.
     */
    static subscribeToMultipleStaffSchedules(companyId, staffIds, startDate, endDate, callback) {
        const unsubscribers = [];

        staffIds.forEach(staffId => {
            const unsubscribe = this.subscribeToStaffSchedule(
                companyId,
                staffId,
                startDate,
                endDate,
                (result) => {
                    callback({
                        ...result,
                        staffId
                    });
                }
            );

            if (unsubscribe) {
                unsubscribers.push(unsubscribe);
            }
        });

        return () => {
            unsubscribers.forEach(unsubscribe => unsubscribe());
        };
    }

    /**
     * Subscribe to staff schedule for multiple weeks (3-week view).
     */
    static subscribeToStaffScheduleMultiWeek(companyId, staffId, weekStarts, callback) {
        try {
            const allDates = [];
            weekStarts.forEach(weekStart => {
                const weekBoundaries = ScheduleUtils.getWeekBoundaries(weekStart);
                allDates.push(weekBoundaries.start, weekBoundaries.end);
            });

            const startDate = allDates.sort()[0];
            const endDate   = allDates.sort()[allDates.length - 1];

            return this.subscribeToStaffSchedule(companyId, staffId, startDate, endDate, callback);

        } catch (error) {
            console.error('Error setting up multi-week schedule subscription:', error);
            return null;
        }
    }

    // ==================== Permission Helpers ====================

    /**
     * Get staff IDs that a user can manage based on role and departments.
     */
    static async getManageableStaffIds(companyId, userRole, userDepartmentIds = []) {
        try {
            const staffCollection = collection(db, 'companies', companyId, 'staff');
            let q = query(staffCollection);

            if (userRole === 'manager' && userDepartmentIds.length > 0) {
                q = query(staffCollection, where('departmentIds', 'array-contains-any', userDepartmentIds));
            }

            const querySnapshot = await getDocs(q);
            const staffIds = querySnapshot.docs.map(doc => doc.id);

            return {
                success:  true,
                staffIds
            };
        } catch (error) {
            console.error('Error getting manageable staff IDs:', error);
            return {
                success:  false,
                error:    error.message,
                staffIds: []
            };
        }
    }

    // ==================== Time Slot Utility Methods ====================

    /**
     * Calculate time slot statistics for shifts.
     */
    static calculateTimeSlotStatistics(shifts) {
        const stats = {
            totalSlots:           0,
            averageSlotsPerShift: 0,
            mostCommonStartTime:  null,
            mostCommonEndTime:    null,
            timeDistribution:     {}
        };

        if (!shifts || shifts.length === 0) {
            return stats;
        }

        const startTimes = {};
        const endTimes   = {};

        shifts.forEach(shift => {
            const startTime = ScheduleUtils.formatTime(shift.startTime);
            const endTime   = ScheduleUtils.formatTime(shift.endTime);

            startTimes[startTime] = (startTimes[startTime] || 0) + 1;
            endTimes[endTime]     = (endTimes[endTime] || 0) + 1;

            const duration = ScheduleUtils.calculateDurationHours(startTime, endTime);
            const slots    = Math.ceil(duration * (60 / this.TIME_SLOT_MINUTES));
            stats.totalSlots += slots;
        });

        stats.averageSlotsPerShift = shifts.length > 0 ? stats.totalSlots / shifts.length : 0;

        stats.mostCommonStartTime = Object.keys(startTimes).reduce((a, b) =>
            startTimes[a] > startTimes[b] ? a : b, null);
        stats.mostCommonEndTime = Object.keys(endTimes).reduce((a, b) =>
            endTimes[a] > endTimes[b] ? a : b, null);

        stats.timeDistribution = {
            startTimes,
            endTimes
        };

        return stats;
    }

    /**
     * Get time slot utilization for a date range.
     */
    static async getTimeSlotUtilization(companyId, staffIds, startDate, endDate) {
        try {
            const utilizationData = {};

            for (const staffId of staffIds) {
                const shiftsResult = await this.getScheduleForStaff(companyId, staffId, startDate, endDate);
                if (!shiftsResult.success) continue;

                const occupiedSlots = {};
                shiftsResult.shifts.forEach(shift => {
                    const startTime = ScheduleUtils.formatTime(shift.startTime);
                    const endTime   = ScheduleUtils.formatTime(shift.endTime);
                    const slots     = ScheduleUtils.getTimeSlotsInRange(startTime, endTime);

                    slots.forEach(slot => {
                        if (!occupiedSlots[shift.date]) {
                            occupiedSlots[shift.date] = [];
                        }
                        occupiedSlots[shift.date].push(slot);
                    });
                });

                utilizationData[staffId] = {
                    occupiedSlots,
                    totalHours:  shiftsResult.shifts.reduce((sum, shift) => sum + shift.expectedHours, 0),
                    shiftCount:  shiftsResult.shifts.length
                };
            }

            return {
                success:     true,
                utilization: utilizationData
            };
        } catch (error) {
            console.error('Error getting time slot utilization:', error);
            return {
                success: false,
                error:   error.message
            };
        }
    }

    // ==================== Enhanced Utility Methods ====================

    /**
     * Get schedule statistics for a period with time slot analysis.
     */
    static async getScheduleStatistics(companyId, staffIds, startDate, endDate) {
        try {
            const schedulesResult = await this.getScheduleForStaffList(companyId, staffIds, startDate, endDate);
            if (!schedulesResult.success) {
                throw new Error(schedulesResult.error);
            }

            const holidaysResult = await this.getHolidays(companyId, startDate, endDate);
            const allShifts      = Object.values(schedulesResult.schedules).flat();

            const statistics = {
                totalShifts:           allShifts.length,
                totalHours:            allShifts.reduce((sum, shift) => sum + shift.expectedHours, 0),
                totalTimeSlots:        0,
                staffCount:            Object.keys(schedulesResult.schedules).length,
                departmentCount:       new Set(allShifts.map(shift => shift.departmentId)).size,
                averageHoursPerShift:  0,
                averageSlotsPerShift:  0,
                holidayCount:          holidaysResult.success ? holidaysResult.holidays.length : 0,
                timeSlotStats:         this.calculateTimeSlotStatistics(allShifts)
            };

            allShifts.forEach(shift => {
                const startTime = ScheduleUtils.formatTime(shift.startTime);
                const endTime   = ScheduleUtils.formatTime(shift.endTime);
                const duration  = ScheduleUtils.calculateDurationHours(startTime, endTime);
                statistics.totalTimeSlots += Math.ceil(duration * (60 / this.TIME_SLOT_MINUTES));
            });

            if (statistics.totalShifts > 0) {
                statistics.averageHoursPerShift = statistics.totalHours / statistics.totalShifts;
                statistics.averageSlotsPerShift = statistics.totalTimeSlots / statistics.totalShifts;
            }

            return {
                success:    true,
                statistics
            };
        } catch (error) {
            console.error('Error getting schedule statistics:', error);
            return {
                success: false,
                error:   error.message
            };
        }
    }

    /**
     * Delete all shifts for a staff member within date range.
     * Internal utility used by saveSchedule. Vacation cascade is handled
     * by saveSchedule via reconcileVacationsWithShifts after the full
     * DELETE-then-CREATE completes, so no cascade fires here.
     */
    static async deleteScheduleRange(companyId, staffId, startDate, endDate) {
        try {
            const shiftsResult = await this.getScheduleForStaff(companyId, staffId, startDate, endDate);
            if (!shiftsResult.success) {
                throw new Error(shiftsResult.error);
            }

            if (shiftsResult.shifts.length === 0) {
                return {
                    success:      true,
                    deletedCount: 0
                };
            }

            const batch = writeBatch(db);
            shiftsResult.shifts.forEach(shift => {
                const shiftRef = doc(db, 'companies', companyId, 'schedules', staffId, 'shifts', shift.id);
                batch.delete(shiftRef);
            });

            await batch.commit();

            return {
                success:      true,
                deletedCount: shiftsResult.shifts.length
            };
        } catch (error) {
            console.error('Error deleting schedule range:', error);
            return {
                success: false,
                error:   error.message
            };
        }
    }
}

export default ScheduleService;